/* app/shell.jsx - Marketlens app shell pieces, composed from Huddle DS.
   Brand · MlSidebar · MlTopBar (site switcher, AI badge, account menu) ·
   ImpersonationBanner. Exposed on window for the mount script. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useRef, useEffect } = React;

  function MlIcon(props) { return React.createElement(window.MlIcon, props); }

  // ---- Brand lockup (mark + wordmark). Mark = indigo tile w/ a "lens" glyph.
  const LensGlyph = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <circle cx="11" cy="11" r="2.6" />
      <path d="M16.5 16.5 21 21" />
    </svg>
  );

  // The brand lockup is Home, it returns to the app (Posts). It used to open
  // Design foundations, which reads as a broken link; foundations stays in the
  // topbar account menu.
  function Brand({ onClick }) {
    return (
      <button className="ml-brand" onClick={onClick} title="MarketLens - go to Posts"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
        <span className="ml-brand__mark" aria-hidden="true"><LensGlyph /></span>
        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
          <span className="ml-brand__name">MarketLens</span>
          <span className="ml-brand__by">by IMYBOSS</span>
        </span>
      </button>
    );
  }

  // ---- Generic click-away menu wrapper
  function Menu({ open, onClose, children, style }) {
    if (!open) return null;
    return (
      <>
        <div className="ml-scrim" onClick={onClose} />
        <div className="ml-menu" style={style} role="menu">{children}</div>
      </>
    );
  }

  // ---- ImyBoss SUITE workspace switcher (SUITE-BOARD SD-001), under the
  //      brand row. "Workspace" here always means the suite-level container
  //      shared across ImyBoss apps (panel orgs), NOT the MarketLens account.
  function PanelWsSwitcher({ list, active, onPick }) {
    const [open, setOpen] = useState(false);
    const name = active ? active.name : "Workspace";
    const initial = active ? active.initial : "W";
    return (
      <div className="pws">
        {open && <div className="pws-scrim" onClick={() => setOpen(false)} />}
        <button className="pws-btn" onClick={() => setOpen(v => !v)} title="Switch workspace">
          <span className="pws-btn__ico" aria-hidden="true">{initial}</span>
          <span className="pws-btn__name">{name}</span>
          <span className="pws-btn__chev" aria-hidden="true"><MlIcon name="selector" size={16} /></span>
        </button>
        {open && (
          <div className="pws-pop" role="menu">
            <div className="pws-pop__label">Workspaces</div>
            {(list || []).map((w) => (
              <button key={w.id} className="pws-opt" onClick={() => { setOpen(false); onPick && onPick(w); }}>
                <span className="pws-opt__ico" aria-hidden="true">{w.initial}</span>
                <span className="pws-opt__name">{w.name}</span>
                {active && w.id === active.id && <span className="pws-opt__check"><MlIcon name="check" size={16} /></span>}
              </button>
            ))}
            <div className="pws-pop__sep" />
            {/* Apps do not create suite workspaces themselves: the panel does. */}
            <a className="pws-opt" href="https://panel.imyboss.app" target="_blank" rel="noreferrer">
              <span className="pws-opt__ico pws-opt__ico--new" aria-hidden="true"><MlIcon name="plus" size={13} /></span>
              <span className="pws-opt__name">Create workspace</span>
              <span className="pws-opt__ext" aria-hidden="true"><MlIcon name="external-link" size={14} /></span>
            </a>
          </div>
        )}
      </div>
    );
  }

  // ---- Site-context pill (sidebar) - shows active edition, opens switcher
  function SitePill({ site, onClick }) {
    return (
      <button className="ml-sitepill" onClick={onClick} title="Switch site">
        <span className="ml-sitepill__flag">{site.flag}</span>
        <span className="ml-sitepill__meta">
          <span className="ml-sitepill__name">{site.name}</span>
          <span className="ml-sitepill__url">{site.url}</span>
        </span>
        <MlIcon name="selector" size={16} />
      </button>
    );
  }

  // ---- Account block (sidebar footer) - real user identity + logout
  function AccountBlock({ onFoundations, user, onLogout, onConsole }) {
    const [open, setOpen] = useState(false);
    const name = (user && user.name) || (user && user.email) || "Account";
    const email = (user && user.email) || "";
    const initials = (name.match(/\b\w/g) || ["?"]).join("").slice(0, 2).toUpperCase();
    const nav = (fn, arg) => { setOpen(false); fn && fn(arg); };
    return (
      <div style={{ position: "relative" }}>
        <button className="ml-account" onClick={() => setOpen(v => !v)} style={{ width: "100%", border: "none", background: "none" }}>
          <span className="hds-avatar hds-avatar--sm" aria-hidden="true">{initials}</span>
          <span className="ml-account__meta">
            <span className="ml-account__name">{name}</span>
            {email && <span className="ml-account__email">{email}</span>}
          </span>
          <MlIcon name="selector" size={16} />
        </button>
        <Menu open={open} onClose={() => setOpen(false)} style={{ bottom: 52, left: 6, right: 6 }}>
          <div className="ml-menu__label">Account</div>
          <button className="ml-menu__item" onClick={() => nav(onConsole, "settings")}><MlIcon name="user" size={16} /> Profile & credentials</button>
          <button className="ml-menu__item" onClick={() => nav(onConsole, "settings")}><MlIcon name="key" size={16} /> Sessions & 2FA</button>
          <div className="ml-menu__sep" />
          <div className="ml-menu__label">Account</div>
          <button className="ml-menu__item" onClick={() => nav(onConsole, "properties")}><MlIcon name="settings" size={16} /> Account console</button>
          <div className="ml-menu__sep" />
          <button className="ml-menu__item ml-menu__item--danger" onClick={() => nav(onLogout)}><MlIcon name="log-out" size={16} /> Sign out</button>
        </Menu>
        <div className="ml-account-foot">
          <button className="ml-foundations-link" onClick={onFoundations}>
            <MlIcon name="palette" size={13} /> Design foundations
          </button>
          <span style={{ fontSize: 11, color: "var(--text-disabled)" }}>v2.0</span>
        </div>
      </div>
    );
  }

  // ---- Sidebar (custom markup reusing hds-sidebar classes; adds site pill,
  //      account block + resize handle that the DS Sidebar does not ship)
  // Note: the panel workspace switcher deliberately does NOT live here. Switching
  // workspace rescopes the whole tenant, which belongs with the other
  // account-level controls, it renders on the console sidebar only (console.jsx),
  // reusing the PanelWsSwitcher exported below.
  function MlSidebar({ groups, active, onNavigate, collapsed, onToggle, site, onSwitchSite, onBrand, onFoundations, open, width, onResize, user, onLogout, onConsole }) {
    const handleRef = useRef(null);
    useEffect(() => {
      const h = handleRef.current; if (!h) return;
      let startX, startW, dragging = false;
      const down = (e) => { dragging = true; startX = e.clientX; startW = width; h.classList.add("ml-resize-handle--dragging"); document.body.style.userSelect = "none"; document.addEventListener("mousemove", move); document.addEventListener("mouseup", up); };
      const move = (e) => { if (!dragging) return; const w = Math.max(232, Math.min(360, startW + (e.clientX - startX))); onResize(w); };
      const up = () => { dragging = false; h.classList.remove("ml-resize-handle--dragging"); document.body.style.userSelect = ""; document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
      h.addEventListener("mousedown", down);
      return () => h.removeEventListener("mousedown", down);
    }, [width, onResize]);

    return (
      <aside className={["hds-sidebar", "ml-sidebar", collapsed ? "hds-sidebar--collapsed" : "", open ? "ml-sidebar--open" : ""].filter(Boolean).join(" ")}>
        <div className="hds-sidebar__brand">
          {/* SD-001 brand row: expanded = lockup + collapse toggle at the far
              right; collapsed = the icon tile itself expands the sidebar. */}
          {!collapsed && <Brand onClick={onBrand} />}
          {!collapsed && <button className="ml-sidebar-toggle" onClick={onToggle} title="Collapse sidebar" aria-label="Collapse sidebar"><MlIcon name="panel-left" size={18} /></button>}
          {collapsed && (
            <button className="ml-brand__mark ml-brand__mark--btn" onClick={onToggle} title="Expand sidebar" aria-label="Expand sidebar">
              <LensGlyph />
            </button>
          )}
        </div>
        <nav className="hds-sidebar__nav" style={{ marginTop: 4 }}>
          {groups.map((g) => (
            <div className="hds-navgroup" key={g.label}>
              {g.label && <div className="hds-navgroup__label">{g.label}</div>}
              {g.items.map((it) => {
                const isActive = it.id === active;
                return (
                  <a key={it.id} href="#"
                     className={["hds-navitem", isActive ? "hds-navitem--active" : ""].filter(Boolean).join(" ")}
                     aria-current={isActive ? "page" : undefined}
                     title={collapsed ? it.label : undefined}
                     onClick={(e) => { e.preventDefault(); onNavigate(it.id); }}>
                    <MlIcon name={it.icon} size={18} />
                    <span>{it.label}</span>
                    {it.badge != null && <span className={["hds-navitem__badge", it.id === "inbox" ? "ml-badge-amber" : ""].filter(Boolean).join(" ")}>{it.badge}</span>}
                  </a>
                );
              })}
            </div>
          ))}
        </nav>
        {!collapsed && <div className="ml-resize-handle" ref={handleRef} aria-hidden="true" />}
      </aside>
    );
  }

  // ---- Site switcher dropdown (anchored in topbar). Property-scoped: `sites`
  //      is only the sites of the ACTIVE property (same domain root); moving to
  //      another property happens in the console ("All properties").
  //
  // THE SWITCHER IS A FILTER AND A FILTER HAS AN ALL POSITION, which is step 5
  // of docs/PROPERTY-DB-MIGRATION.md and the reason this menu gained a row.
  // Before it, selecting a section WAS selecting a database scope, so there was
  // no position from which a customer could see their whole website: standing
  // on the main site they could not see the blog's data and standing on the
  // blog they could not see the front end's.
  //
  // THE ALL ROW IS THE WEBSITE, NOT A SITE, and it is labelled as the website
  // for that reason. `sectionId` null is that position (MODULE-SCOPE 1.1). It
  // is not the same as "All properties" at the bottom, which leaves this
  // website for the console, and the two are kept visibly apart: one is a
  // position of this control and the other is a way out of it.
  function SiteSwitcher({ site, sites, onPick, onAddSite, propertyLabel, onAllProperties, sectionId, onPickAll }) {
    const [open, setOpen] = useState(false);
    // Network editions show a country flag; standalone (single) sites show a
    // neutral globe icon instead of an implied country.
    const siteIcon = (s) => (s.wp_type === "network" ? <span style={{ fontSize: 16 }}>{s.flag}</span> : <MlIcon name="globe" size={16} />);
    // The All position is only offered when the switcher is a filter over
    // something: a property with one section has nothing to filter, so the row
    // would be a second name for the site already selected, which is how two
    // entries for one subject come to disagree about what the website is.
    const filterable = !!onPickAll && sites.length > 1;
    const all = filterable && sectionId == null;
    const label = propertyLabel || "Sites";
    return (
      <div style={{ position: "relative" }}>
        <button className="ml-siteswitch" onClick={() => setOpen(v => !v)}>
          <span className="ml-siteswitch__flag">{all ? <MlIcon name="layers" size={16} /> : siteIcon(site)}</span>
          <span className="ml-siteswitch__meta">
            <span className="ml-siteswitch__name">{all ? label : site.name}</span>
            <span className="ml-siteswitch__sub">{all ? `whole website · ${sites.length} sections` : site.url}</span>
          </span>
          <MlIcon name="selector" size={16} />
        </button>
        <Menu open={open} onClose={() => setOpen(false)} style={{ top: 46, left: 0, minWidth: 280 }}>
          <div className="ml-menu__label">{label}</div>
          {filterable && (
            <button className={["ml-menu__item", all ? "ml-menu__item--active" : ""].filter(Boolean).join(" ")}
                    onClick={() => { onPickAll(); setOpen(false); }}>
              <MlIcon name="layers" size={16} />
              <span className="ml-menu__item-meta">
                <span style={{ fontWeight: 600 }}>The whole website</span>
                <small>every section · {sites.length} of them</small>
              </span>
              {all && <span className="ml-menu__check"><MlIcon name="check" size={16} /></span>}
            </button>
          )}
          {sites.map((s) => (
            <button key={s.id} className={["ml-menu__item", (!all && s.id === site.id) ? "ml-menu__item--active" : ""].filter(Boolean).join(" ")}
                    onClick={() => { onPick(s.id); setOpen(false); }}>
              {siteIcon(s)}
              <span className="ml-menu__item-meta">
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                <small>
                  {s.url}
                  {/* What this section IS, which is what decides what can be
                      done to its rows. A section with no CMS is read by
                      crawling it and can never be published to, and saying so
                      in the switcher is cheaper than a customer finding out on
                      a screen that cannot save. */}
                  {s.source === "crawl" ? " · no CMS" : (s.wp_type === "network" ? " · " + s.lang : " · single site")}
                </small>
              </span>
              {s.over && <span className="hds-badge hds-badge--warning" style={{ marginLeft: "auto" }}>Over limit</span>}
              {!all && s.id === site.id && !s.over && <span className="ml-menu__check"><MlIcon name="check" size={16} /></span>}
            </button>
          ))}
          <div className="ml-menu__sep" />
          {onAllProperties && <button className="ml-menu__item" onClick={() => { setOpen(false); onAllProperties(); }}><MlIcon name="layers" size={16} /> All properties</button>}
          <button className="ml-menu__item" onClick={() => { setOpen(false); onAddSite && onAddSite(); }}><MlIcon name="plus" size={16} /> Add a site</button>
        </Menu>
      </div>
    );
  }

  // ---- AI model badge
  // The catalog and the current pick both come from the server (GET /account/ai-model)
  // so the menu can never offer a model the backend would reject, and the badge
  // always names the model the AI routes will actually call.
  function AiBadge({ onConsole }) {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(null);   // { model, source, choices, locked }
    const [saving, setSaving] = useState(null); // id being written
    const [err, setErr] = useState(null);

    useEffect(() => {
      let alive = true;
      api('/account/ai-model', { silent401: true }).then(r => { if (alive) setState(r); }).catch(() => {});
      return () => { alive = false; };
    }, []);

    const choices = (state && state.choices) || [];
    const current = choices.find(c => c.id === (state && state.model));
    const label = current ? current.label : (state && state.model) || "AI model";

    const pick = async (id) => {
      if (!state || id === state.model || state.locked) return;
      setSaving(id); setErr(null);
      try {
        const r = await api('/account/ai-model', { method: 'POST', body: JSON.stringify({ model: id }) });
        setState(s => ({ ...s, model: r.model, source: r.source }));
        setOpen(false);
      } catch (e) { setErr(e.message || 'Could not change the model'); }
      finally { setSaving(null); }
    };

    return (
      <div style={{ position: "relative" }}>
        <button className="ml-aibadge" onClick={() => setOpen(v => !v)} title={`AI model: ${label}`}>
          <span className="ml-aibadge__dot" />
          <MlIcon name="sparkles" size={14} />
          <span className="ml-aibadge__text">{label}</span>
        </button>
        <Menu open={open} onClose={() => setOpen(false)} style={{ top: 42, right: 0, minWidth: 268 }}>
          <div className="ml-menu__label">AI model - BYO Anthropic key</div>
          {!state && <div className="ml-menu__note">Loading…</div>}
          {choices.map(c => (
            <button key={c.id} disabled={state.locked || saving} onClick={() => pick(c.id)}
                    className={"ml-menu__item" + (c.id === state.model ? " ml-menu__item--active" : "")}>
              <MlIcon name={c.icon} size={16} />
              <span className="ml-menu__item-meta"><span style={{ fontWeight: 600 }}>{c.label}</span><small>{c.blurb}</small></span>
              {saving === c.id
                ? <span className="ml-menu__check"><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} /></span>
                : c.id === state.model ? <span className="ml-menu__check"><MlIcon name="check" size={16} /></span> : null}
            </button>
          ))}
          {state && state.locked && <div className="ml-menu__note">Pinned by this deployment (ANTHROPIC_MODEL): unset it to choose here.</div>}
          {/* A pinned id the backend has no facts about still gets called, but
              with thinking forced off and a reduced output budget, which the
              user sees as AI answers that come back cut off. The person who can
              fix that is the one reading this menu. */}
          {state && state.locked && state.recognised === false && (
            <div className="ml-menu__note ml-menu__note--danger">
              This build does not recognise “{state.model}”. It is still used, but with a smaller output budget, so long AI answers can come back cut off. Pin a known id, or unset ANTHROPIC_MODEL.
            </div>
          )}
          {err && <div className="ml-menu__note ml-menu__note--danger">{err}</div>}
          <div className="ml-menu__sep" />
          <button className="ml-menu__item" onClick={() => { setOpen(false); onConsole && onConsole("settings"); }}><MlIcon name="key" size={16} /> Manage API key</button>
        </Menu>
      </div>
    );
  }

  // ---- Account avatar + menu (top-right). Real user identity.
  function TopAccount({ user, onLogout, onConsole, onFoundations }) {
    const [open, setOpen] = useState(false);
    const name = (user && user.name) || (user && user.email) || "Account";
    const email = (user && user.email) || "";
    const initials = (name.match(/\b\w/g) || ["?"]).join("").slice(0, 2).toUpperCase();
    const nav = (fn, arg) => { setOpen(false); fn && fn(arg); };
    return (
      <div style={{ position: "relative" }}>
        <button className="ml-topavatar" onClick={() => setOpen(v => !v)} title={name} aria-label="Account menu">
          <span className="hds-avatar hds-avatar--sm" aria-hidden="true">{initials}</span>
        </button>
        <Menu open={open} onClose={() => setOpen(false)} style={{ top: 46, right: 0, minWidth: 240 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px 10px" }}>
            <span className="hds-avatar hds-avatar--sm" aria-hidden="true">{initials}</span>
            <span className="ml-account__meta"><span className="ml-account__name">{name}</span>{email && <span className="ml-account__email">{email}</span>}</span>
          </div>
          <div className="ml-menu__sep" />
          <button className="ml-menu__item" onClick={() => nav(onConsole, "settings")}><MlIcon name="user" size={16} /> Profile & credentials</button>
          <button className="ml-menu__item" onClick={() => nav(onConsole, "settings")}><MlIcon name="key" size={16} /> Sessions & 2FA</button>
          <div className="ml-menu__sep" />
          <div className="ml-menu__label">Account</div>
          <button className="ml-menu__item" onClick={() => nav(onConsole, "properties")}><MlIcon name="settings" size={16} /> Account console</button>
          <button className="ml-menu__item" onClick={() => nav(onFoundations)}><MlIcon name="palette" size={16} /> Design foundations</button>
          <div className="ml-menu__sep" />
          <button className="ml-menu__item ml-menu__item--danger" onClick={() => nav(onLogout)}><MlIcon name="log-out" size={16} /> Sign out</button>
        </Menu>
      </div>
    );
  }

  // ---- Top bar
  function MlTopBar({ site, sites, onPickSite, onToggleSidebar, onSearch, user, onLogout, onConsole, onFoundations, onAddSite, readOnly, propertyLabel, moduleId, assistOpen, onToggleAssist, sectionId, onPickAll }) {
    const { IconButton } = window.HuddleDesignSystem_775113;
    // Reached at render time, not import time, so module order does not matter.
    const AiAssistTrigger = window.MlAiDrawer && window.MlAiDrawer.AiAssistTrigger;
    return (
      <header className="hds-topbar">
        {/* Mobile-only (<=960px) drawer opener: the sidebar COLLAPSE toggle
            lives in the sidebar brand row, never in the topbar (SD-001). */}
        <span className="ml-topbar__collapse"><IconButton icon="panel-left" label="Open navigation" size="sm" onClick={onToggleSidebar} /></span>
        <SiteSwitcher site={site} sites={sites} onPick={onPickSite} onAddSite={onAddSite}
                      sectionId={sectionId} onPickAll={onPickAll}
                      propertyLabel={propertyLabel} onAllProperties={onConsole ? () => onConsole("properties") : null} />
        <div className="hds-topbar__search ml-topbar-search-hide" style={{ marginLeft: 8 }}>
          <div className="hds-input-group">
            <span className="hds-input-group__icon"><MlIcon name="search" size={16} /></span>
            <input className="hds-input" placeholder="Search posts, keywords, entities…" onChange={(e) => onSearch && onSearch(e.target.value)} aria-label="Search" />
          </div>
        </div>
        <div className="hds-topbar__spacer" />
        <div className="hds-topbar__actions">
          {readOnly && <span className="ml-ro-badge" title="Your role has read-only access"><MlIcon name="eye" size={14} />Read-only</span>}
          <button type="button" className="hds-btn hds-btn--secondary hds-btn--sm" onClick={() => onConsole && onConsole("properties")}>Account console<MlIcon name="arrow-right" size={16} /></button>
          <AiBadge onConsole={onConsole} />
          {/* AI assistance for the screen the user is on, next to the badge
              naming the model that will answer. Only the trigger lives here:
              the panel is a column of the shell (main.jsx), which is what lets
              it sit BESIDE the screen rather than over it.
              The sidebar's AI Assistant entry is the site-wide chat and is
              untouched by this. Not offered to a read-only role: asking costs
              a POST, which both api() and enforceReadOnly refuse, so the
              button could only ever open a panel that answers "your access is
              read-only". */}
          {AiAssistTrigger && !readOnly && (
            <AiAssistTrigger site={site} moduleId={moduleId} open={assistOpen} onToggle={onToggleAssist} />
          )}
          <IconButton icon="bell" label="Notifications" bordered size="sm" />
          <span className="hds-topbar__divider" />
          <TopAccount user={user} onLogout={onLogout} onConsole={onConsole} onFoundations={onFoundations} />
        </div>
      </header>
    );
  }

  // ---- Impersonation ("acting as tenant") banner
  function ImpersonationBanner({ tenant, onExit }) {
    return (
      <div className="ml-impersonation" role="status">
        <MlIcon name="eye" size={16} />
        <span>You are acting as <strong>{tenant}</strong>. Changes are attributed to this tenant.</span>
        <span className="ml-impersonation__spacer" />
        <button className="ml-impersonation__exit" onClick={onExit}><MlIcon name="x" size={14} /> Exit tenant view</button>
      </div>
    );
  }

  window.MlShell = { Brand, MlSidebar, MlTopBar, ImpersonationBanner, Menu, PanelWsSwitcher };
})();
